<script>
import IconButton from "@/components/shared/IconButton.vue";
import { capitalize } from "@/helpers/filters";
import { canCancelLoan, canResumeLoan } from "@/helpers/permissions/loans";
import { put } from "@/requests/server";
import dayjs from "dayjs";
import { sections } from "./FullLoanSections";

export default {
  name: "LoanButtons",
  components: { IconButton },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  computed: {
    loanIsInProcess() {
      return (
        this.loan.id &&
        this.loan.id !== "new" &&
        this.loan.status !== "canceled" &&
        this.loan.status !== "rejected" &&
        this.loan.status !== "completed"
      );
    },
    canCancelLoan() {
      return canCancelLoan(this.$store.state.user, this.loan);
    },
    canResumeLoan() {
      return canResumeLoan(this.$store.state.user, this.loan);
    },
    canAddIncident() {
      return (
        // No new incidents.
        this.loanIsInProcess &&
        this.loan.loanable.active_incidents.filter((i) => !i.id).length === 0
      );
    },
    canCompleteContribution() {
      return this.loan.status === "validated" && this.loan.contribution_only;
    },
  },
  methods: {
    async cancelLoan() {
      let cancel = await this.$bvModal.msgBoxConfirm(
        this.$t("components.loan.buttons.confirm_cancel.title"),
        {
          okTitle: this.$t("components.loan.buttons.confirm_cancel.ok"),
          cancelTitle: this.$t("components.loan.buttons.confirm_cancel.cancel"),
          okVariant: "danger",
          cancelVariant: "ghost-secondary",
        }
      );
      if (!cancel) {
        return;
      }
      const { data } = await put(`loans/${this.loan.id}/cancel`, null, {
        notifications: { action: this.$t("components.loan.buttons.notifications.cancel") },
      });
      this.$emit("input", data);
    },
    jumpToContribution() {
      this.$emit("jump", {
        tab: "cost",
        target: "loan-section-cost",
      });
    },
    async resumeLoan() {
      const h = this.$createElement;

      const contentText = [h("p", this.$t("components.loan.buttons.confirm_resume.title"))];
      if (
        (!this.loan.accepted_at || !this.loan.prepaid_at) &&
        dayjs(this.loan.actual_return_at).isBefore(dayjs())
      ) {
        contentText.push(
          h(
            "small",
            { class: "text-muted" },
            this.$t("components.loan.buttons.confirm_resume.note")
          )
        );
      }

      const content = h("div", {}, contentText);

      let resume = await this.$bvModal.msgBoxConfirm([content], {
        okTitle: capitalize(this.$t("components.loan.buttons.confirm_resume.ok")),
        cancelTitle: capitalize(this.$t("forms.cancel")),
        okVariant: "warning",
        cancelVariant: "ghost-secondary",
      });
      if (!resume) {
        return;
      }
      const { data } = await put(`loans/${this.loan.id}/resume`, null, {
        notifications: { action: this.$t("components.loan.buttons.notifications.resume") },
      });
      this.$emit("input", data);
    },
    addIncident() {
      if (!this.canAddIncident) {
        return;
      }
      this.$emit(
        "input",
        {
          ...this.loan,
          loanable: {
            ...this.loan.loanable,
            active_incidents: [
              ...this.loan.loanable.active_incidents,
              {
                status: "in_process",
                incident_type: null,
                loan_id: this.loan.id,
              },
            ],
          },
        },
        {
          refreshDashboard: false,
          freshlyLoaded: false,
        }
      );
      this.$emit("jump", sections.incidents);
    },
  },
};
</script>

<template>
  <div v-if="loanIsInProcess" class="loan-buttons">
    <icon-button
      v-if="canCompleteContribution"
      size="sm"
      variant="outline-success"
      icon="check-circle"
      square
      @click="jumpToContribution"
    >
      {{ $t("components.loan.buttons.complete") | capitalize }}
    </icon-button>

    <div class="loan-buttons__row">
      <icon-button
        size="sm"
        variant="outline-warning"
        icon="exclamation-triangle"
        square
        @click="addIncident"
        >{{ $t("components.loan.buttons.report_incident") }}</icon-button
      >
      <icon-button
        size="sm"
        variant="outline-danger"
        icon="x-octagon"
        :onclick="cancelLoan"
        :disabled="!canCancelLoan"
        square
      >
        {{ $t("forms.cancel") | capitalize }}
      </icon-button>
    </div>
  </div>
  <icon-button
    v-else-if="canResumeLoan"
    size="sm"
    variant="outline-warning"
    icon="play-circle"
    :onclick="resumeLoan"
    square
  >
    {{ $t("components.loan.buttons.resume") | capitalize }}
  </icon-button>
</template>

<style scoped lang="scss">
.loan-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: stretch;
  > * {
    flex-grow: 1;
  }

  &__row {
    display: flex;
    gap: 1rem;

    > * {
      flex-grow: 1;
    }
  }
}
</style>
